import { useEffect, useMemo, useState } from 'react';
import { formatISO } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProduct } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase, ProductAlias } from '@ibk/shared-types';
import { formatCurrency } from '@ibk/shared-utils';
import { PixKeyTypeDescription, PixType } from '../../enums';
import { usePostPix } from '../usePostPix';
import { usePostValidateDuplicate } from '../usePostValidateDuplicate';
import { usePostValidatePixQrCodeKey } from '../usePostValidatePixQrCodeKey';
const PIX_CODE_STEP = 0;
const VALUE_STEP = 1;
const REVISION_STEP = 2;
const SUCCESS_STEP = 3;
export function usePixCopyPaste(canSeeBalance) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const { postValidateQrCodePixKey, qrCodePixKey, error: errorValidatePixQrCodeKey, setError: setErrorValidatePixQrCodeKey, loading: loadingValidatePixQrCodeKey, } = usePostValidatePixQrCodeKey();
    const { postValidateDuplicate, isDuplicate, loading: loadingValidateDuplicate } = usePostValidateDuplicate();
    const { postPix, pix, pixScheduled, pixError, pixScheduledError, loading: loadingPostPix } = usePostPix();
    const { data: product } = useProduct(ProductAlias.CONTA_DIGITAL, {
        enabled: !!canSeeBalance,
    });
    const [step, setStep] = useState(PIX_CODE_STEP);
    const [alert, setAlert] = useState(undefined);
    const [pixCode, setPixCode] = useState(undefined);
    const [value, setValue] = useState(undefined);
    const [paymentDate, setPaymentDate] = useState(undefined);
    const [transactionStartAt, setTransactionStartAt] = useState(new Date().toISOString());
    const [showCalendar, setShowCalendar] = useState(false);
    const [showPinKeyboard, setShowPinKeyboard] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [confirmDuplicate, setConfirmDuplicate] = useState(false);
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [showPixTimeoutError, setShowPixTimeoutError] = useState(false);
    const [showPixLimitExceededModal, setShowPixLimitExceededModal] = useState(false);
    const [showPaymentAlreadyExistsModal, setShowPaymentAlreadyExistsModal] = useState(false);
    useEffect(() => {
        if (qrCodePixKey) {
            if (!qrCodePixKey.amount || qrCodePixKey.amount <= 0) {
                setStep(VALUE_STEP);
            }
            else {
                setStep(REVISION_STEP);
            }
        }
    }, [qrCodePixKey]);
    useEffect(() => {
        if (pix) {
            setShowPinKeyboard(false);
            setStep(SUCCESS_STEP);
        }
    }, [pix]);
    useEffect(() => {
        if (pixScheduled) {
            setShowPinKeyboard(false);
            setStep(SUCCESS_STEP);
        }
    }, [pixScheduled]);
    useEffect(() => {
        if (errorValidatePixQrCodeKey) {
            setAlert({
                message: errorValidatePixQrCodeKey,
                appearance: 'error',
            });
        }
    }, [errorValidatePixQrCodeKey]);
    useEffect(() => {
        if (pixError) {
            onError(pixError);
        }
    }, [pixError]);
    useEffect(() => {
        if (pixScheduledError) {
            onError(pixScheduledError);
        }
    }, [pixScheduledError]);
    useEffect(() => {
        if (state?.code) {
            setPixCode(state.code);
        }
    }, [state?.code]);
    useEffect(() => {
        if (isDuplicate === true) {
            setShowDuplicateModal(true);
        }
        else if (isDuplicate === false) {
            setShowPinKeyboard(true);
        }
    }, [isDuplicate]);
    const loading = useMemo(() => loadingValidatePixQrCodeKey || loadingPostPix || loadingValidateDuplicate, [loadingValidatePixQrCodeKey, loadingPostPix, loadingValidateDuplicate]);
    const balance = useMemo(() => {
        return formatCurrency(product?.metadata?.balance?.amount ?? 0);
    }, [product]);
    const isPixCodeStep = useMemo(() => step === PIX_CODE_STEP, [step]);
    const isValueStep = useMemo(() => step === VALUE_STEP, [step]);
    const isRevisionStep = useMemo(() => step === REVISION_STEP, [step]);
    const isSuccessStep = useMemo(() => step === SUCCESS_STEP, [step]);
    const isTimeoutError = useMemo(() => {
        return showPixTimeoutError;
    }, [showPixTimeoutError]);
    const isPixCodeStepNextDisabled = useMemo(() => !!(!pixCode || errorValidatePixQrCodeKey), [pixCode, errorValidatePixQrCodeKey]);
    const isValueStepNextDisabled = useMemo(() => {
        const availableBalance = product?.metadata?.balance?.amount;
        return !value || !availableBalance || value > availableBalance;
    }, [value, product]);
    const isPendingTransaction = useMemo(() => {
        if (pix) {
            return !pix.transaction?.transactionId;
        }
        if (pixScheduled) {
            return pixScheduled?.isApproved === false;
        }
        return false;
    }, [pix, pixScheduled]);
    const showStepperHorizontal = useMemo(() => {
        return !isSuccessStep && !isTimeoutError;
    }, [isSuccessStep, isTimeoutError]);
    const successStepTitle = useMemo(() => {
        if (isPendingTransaction) {
            if (paymentDate) {
                return t('billet-was-scheduled-info-operator-title');
            }
            return t('billet-was-paid-info-operator-title');
        }
        return t('all-very-well');
    }, [isPendingTransaction, paymentDate]);
    const successStepCaption = useMemo(() => {
        if (isPendingTransaction) {
            if (paymentDate) {
                return t('billet-was-scheduled-info-operator-description');
            }
            return t('billet-was-paid-info-operator-description');
        }
        if (paymentDate) {
            return t('pix-payment-scheduled-with-success');
        }
        return t('pix-payment-done-with-success');
    }, [isPendingTransaction, paymentDate]);
    const successStepBackButtonLabel = useMemo(() => {
        if (isPendingTransaction) {
            return t('back-to-area-pix');
        }
        else {
            return t('show-receipt');
        }
    }, [isPendingTransaction, paymentDate]);
    const isPixLimitExceededModalVisible = useMemo(() => {
        return isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_PIX_LIMITS) && showPixLimitExceededModal;
    }, [showPixLimitExceededModal]);
    function onError(error) {
        setShowPinKeyboard(false);
        if (error === t('payment-in-processing')) {
            setShowPaymentAlreadyExistsModal(true);
        }
        else if (error === t('pix-limit-exceeded')) {
            setShowPixLimitExceededModal(true);
        }
        else if (error === t('pix-timeout-error')) {
            setShowPixTimeoutError(true);
        }
        else if (error === t('insufficient-balance-modal-title')) {
            setShowInsufficientBalanceModal(true);
        }
        else {
            setAlert({
                message: error,
                appearance: 'error',
            });
        }
    }
    function onChangePixCodeInput(value) {
        setPixCode(value);
        setErrorValidatePixQrCodeKey(undefined);
    }
    function onPressNextOnPixCodeStep() {
        postValidateQrCodePixKey(pixCode);
    }
    function onPressBackOnPixCodeStep() {
        navigate(-1);
    }
    function onPressBackOnValueStep() {
        setStep(PIX_CODE_STEP);
    }
    function onPressNextOnValueStep() {
        setStep(REVISION_STEP);
    }
    function onPressBackOnRevisionStep() {
        if (!qrCodePixKey?.amount || qrCodePixKey.amount <= 0) {
            setStep(VALUE_STEP);
        }
        else {
            setStep(PIX_CODE_STEP);
        }
    }
    function onPressConfirmPaymentOnRevisionStep() {
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAGAMENTOS_DUPLICATE_PIX, undefined, cache?.data);
        if (isWhiteListed && !paymentDate) {
            postValidateDuplicate({
                date: formatISO(new Date()),
                key: qrCodePixKey?.pixInfo.key,
                keyType: PixKeyTypeDescription.QR_CODE.toString(),
                amount: (value ?? qrCodePixKey?.amount),
                receiverName: qrCodePixKey?.pixInfo.name,
            });
        }
        else {
            setShowPinKeyboard(true);
        }
    }
    async function onPressConfirmPinOnRevisionStep(pin) {
        postPix(pin, transactionStartAt, paymentDate, {
            type: PixType.QR_CODE,
            key: qrCodePixKey?.pixInfo.key,
            qrCodeType: qrCodePixKey?.pixInfo.qrCodeType,
            amount: value ?? qrCodePixKey?.amount,
            receiverName: qrCodePixKey?.pixInfo.name,
            receiverDocumentNumber: qrCodePixKey?.pixInfo.taxIdentifier,
            description: qrCodePixKey?.pixInfo.description,
            endToEndId: qrCodePixKey?.pixInfo.endToEndId,
            transactionIdentification: qrCodePixKey?.pixInfo.transactionIdentification,
            bankName: qrCodePixKey?.pixInfo.bank,
            accountTypeAcronym: qrCodePixKey?.pixInfo.accountType,
            confirmDuplicate,
        });
    }
    function onPressResetOnSuccessStep() {
        setStep(PIX_CODE_STEP);
        setPixCode(undefined);
        setValue(undefined);
        setPaymentDate(undefined);
        setTransactionStartAt(new Date().toISOString());
        setShowCalendar(false);
        setShowPinKeyboard(false);
        setShowReceipt(false);
    }
    function onPressShowReceiptOnSuccessStep() {
        setShowReceipt(true);
    }
    function onPressBackToAreaPixOnSuccessStep() {
        navigate('/conta-pj');
    }
    function onPressBackToExtractOnSuccessStep() {
        navigate('/conta-pj');
    }
    function onPressBackOnSuccessStep() {
        if (isPendingTransaction) {
            onPressBackToAreaPixOnSuccessStep();
        }
        else {
            onPressShowReceiptOnSuccessStep();
        }
    }
    function onConfirmDuplicateOnRevisionStep() {
        setConfirmDuplicate(true);
        setShowPinKeyboard(true);
    }
    function onCloseDuplicateModalOnRevisionStep() {
        setShowDuplicateModal(false);
    }
    return {
        step,
        setStep,
        pixCode,
        setPixCode,
        value,
        setValue,
        paymentDate,
        setPaymentDate,
        alert,
        setAlert,
        showCalendar,
        setShowCalendar,
        showPinKeyboard,
        setShowPinKeyboard,
        showReceipt,
        setShowReceipt,
        showDuplicateModal,
        setShowDuplicateModal,
        confirmDuplicate,
        setConfirmDuplicate,
        showInsufficientBalanceModal,
        setShowInsufficientBalanceModal,
        showPixTimeoutError,
        showPixLimitExceededModal,
        setShowPixLimitExceededModal,
        showPaymentAlreadyExistsModal,
        setShowPaymentAlreadyExistsModal,
        setShowPixTimeoutError,
        onPressNextOnPixCodeStep,
        onPressBackOnPixCodeStep,
        onPressBackOnValueStep,
        onPressNextOnValueStep,
        onPressBackOnRevisionStep,
        onPressConfirmPaymentOnRevisionStep,
        onChangePixCodeInput,
        onPressConfirmPinOnRevisionStep,
        onPressResetOnSuccessStep,
        onPressShowReceiptOnSuccessStep,
        onPressBackToAreaPixOnSuccessStep,
        onPressBackToExtractOnSuccessStep,
        onPressBackOnSuccessStep,
        onConfirmDuplicateOnRevisionStep,
        onCloseDuplicateModalOnRevisionStep,
        qrCodePixKey,
        pix,
        pixScheduled,
        balance,
        loading,
        isPixCodeStep,
        isValueStep,
        isRevisionStep,
        isPixCodeStepNextDisabled,
        isValueStepNextDisabled,
        isSuccessStep,
        isPendingTransaction,
        isTimeoutError,
        successStepTitle,
        successStepCaption,
        successStepBackButtonLabel,
        showStepperHorizontal,
        isPixLimitExceededModalVisible,
    };
}
